import { Building, Eye, Keyboard, LockKeyhole, Palette, Shield, User, type LucideIcon } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { AccessibilityPage } from "@/features/account/AccessibilityPage";
import { PreferencesPage } from "@/features/account/PreferencesPage";
import { ProfilePage } from "@/features/account/ProfilePage";
import { SecurityPage } from "@/features/account/SecurityPage";
import { ShortcutsPage } from "@/features/account/ShortcutsPage";
import { ProfileAvatar } from "@/shared/components/ProfileAvatar";
import { ROLE_LABEL, ROLES } from "@/shared/utils/roles";
import { useAppShell } from "@/shell/app-shell-context";

type Section = "identidade" | "empresas" | "acesso" | "aparencia" | "acessibilidade" | "atalhos" | "seguranca";

const GROUPS: Array<{ title: string; items: Array<{ value: Section; label: string; icon: LucideIcon }> }> = [
  {
    title: "PERFIL",
    items: [
      { value: "identidade", label: "Identidade", icon: User },
      { value: "empresas", label: "Empresas vinculadas", icon: Building },
      { value: "acesso", label: "Nível de acesso", icon: Shield }
    ]
  },
  {
    title: "PREFERÊNCIAS",
    items: [
      { value: "aparencia", label: "Aparência e temas", icon: Palette },
      { value: "acessibilidade", label: "Acessibilidade", icon: Eye },
      { value: "atalhos", label: "Atalhos", icon: Keyboard }
    ]
  },
  { title: "SEGURANÇA", items: [{ value: "seguranca", label: "Senha e sessões", icon: LockKeyhole }] }
];

/** Os nomes antigos de `?aba=` (das três telas separadas) caem na primeira
 *  seção de cada uma. */
const ALIASES: Record<string, Section> = { perfil: "identidade", preferencias: "aparencia" };

function sectionFrom(value: string | null): Section {
  if (!value) return "identidade";
  if (value in ALIASES) return ALIASES[value];
  return GROUPS.some(group => group.items.some(item => item.value === value)) ? (value as Section) : "identidade";
}

/**
 * Configurações da conta (2026-10-15): Perfil, Preferências e Segurança numa
 * tela só, com uma barra lateral no desenho da de Ajustes. A seção fica na URL
 * (`?aba=`) pra o link e o voltar do navegador funcionarem; `/perfil`,
 * `/preferencias` e `/seguranca` redirecionam pra cá. O conteúdo de cada seção é
 * o das telas de antes, montadas sem a barra própria.
 */
export function AccountSettingsPage() {
  const { profile } = useAppShell();
  const [params, setParams] = useSearchParams();
  const active = sectionFrom(params.get("aba"));
  const top = ROLES.find(role => profile.roles.includes(role));
  return (
    <div className="ajustes-layout account-settings">
      <aside className="ajustes-sidebar">
        <div className="ajustes-sidebar-header">
          <div className="avatar account-settings-avatar">
            <ProfileAvatar name={profile.name} avatarUrl={profile.avatar_url} />
          </div>
          <div>
            <strong>{profile.name}</strong>
            <span>{top ? ROLE_LABEL[top] : "Configurações da conta"}</span>
          </div>
        </div>
        <nav className="ajustes-nav" aria-label="Seções das configurações">
          {GROUPS.map(group => (
            <div key={group.title} className="account-settings-group">
              <div className="ajustes-nav-section-title">{group.title}</div>
              {group.items.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  className={`ajustes-nav-item ${value === active ? "active" : ""}`}
                  aria-current={value === active ? "page" : undefined}
                  onClick={() => setParams({ aba: value })}
                >
                  <Icon size={16} />
                  <span className="ajustes-nav-item-body">
                    <span className="ajustes-nav-item-label">{label}</span>
                  </span>
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>
      <main className="ajustes-content">
        {active === "identidade" && <ProfilePage tab="identity" />}
        {active === "empresas" && <ProfilePage tab="companies" />}
        {active === "acesso" && <ProfilePage tab="permissions" />}
        {active === "aparencia" && <PreferencesPage tab="appearance" />}
        {active === "acessibilidade" && <AccessibilityPage />}
        {active === "atalhos" && <ShortcutsPage />}
        {active === "seguranca" && <SecurityPage />}
      </main>
    </div>
  );
}
